import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import toast from "react-hot-toast";
import { UserContext } from "../../UserContext";
import styles from "./InvoiceDetails.module.css";
import InvoicePrintModal from "./InvoicePrintModal";
import {
  FaArrowLeft,
  FaFileInvoice,
  FaPrint,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaClock,
  FaUndo,
  FaInfoCircle,
} from "react-icons/fa";

const InvoiceDetails = () => {
  const { t, i18n } = useTranslation();
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useContext(UserContext);

  const [invoice, setInvoice] = useState(location.state?.invoice || null);
  const [loading, setLoading] = useState(!invoice);
  const [entity, setEntity] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [returnMode, setReturnMode] = useState(false);
  const [returnQuantities, setReturnQuantities] = useState({});
  const [returnReason, setReturnReason] = useState("");
  const [previousReturns, setPreviousReturns] = useState([]);
  const [selectedReturnId, setSelectedReturnId] = useState(null);
  const [processingReturn, setProcessingReturn] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [nextInvoiceCounter, setNextInvoiceCounter] = useState(null);
  const [showAllReturns, setShowAllReturns] = useState(false);
  const [showDeleteReturnModal, setShowDeleteReturnModal] = useState(false);
  const [returnToDelete, setReturnToDelete] = useState(null);
  const [isDeletingReturn, setIsDeletingReturn] = useState(false);

  useEffect(() => {
    if (!invoice && invoiceId) {
      fetchInvoiceDetails();
    }
    if (
      invoice &&
      invoice.accountId &&
      !invoice.businessEntity &&
      !invoice.supplier &&
      !invoice.client
    ) {
      fetchEntityDetails();
    }
    if (invoice?.farmId) {
      fetchWarehouses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, invoice?.farmId]);

  // Separate effect for fetching returns after invoice is loaded
  useEffect(() => {
    if (invoice?.id && invoice?.accountId) {
      console.log("Invoice loaded, fetching returns for invoice:", invoice.id);
      fetchInvoiceReturns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id, invoice?.accountId]);

  useEffect(() => {
    // Set HTML direction based on language
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    try {
      // Fetch invoice by ID directly (matching Android implementation)
      const response = await axios.get(`api/invoice/${invoiceId}`, {
        headers: { Authorization: token },
      });

      // API returns wrapped response with items array
      const apiResponse = response.data;
      console.log("API Response:", apiResponse);

      // Extract invoice from the items array (API returns array even for single item)
      const invoiceData = apiResponse.items?.[0] || apiResponse;
      console.log("Extracted invoice data:", invoiceData);

      if (invoiceData) {
        // Process invoice items to flatten the nested structure
        if (invoiceData.items && Array.isArray(invoiceData.items)) {
          invoiceData.items = invoiceData.items.map((item) => {
            console.log("Processing item:", item);

            // Check if item has nested invoiceDetails structure
            if (item.invoiceDetails) {
              // Flatten the nested invoiceDetails structure
              const processedItem = {
                id: item.invoiceDetails.id,
                invoiceId: item.invoiceDetails.invoiceId,
                inventoryItemDetailId:
                  item.invoiceDetails.inventoryItemDetailId,
                description: item.invoiceDetails.description,
                quantity: item.invoiceDetails.quantity || 1,
                pricePerUnit: item.invoiceDetails.pricePerUnit || 0,
                totalAmount: item.invoiceDetails.totalAmount || 0,
                // Names might be swapped in backend response
                itemNameEn: item.itemNameEn || item.itemNameAr,
                itemNameAr: item.itemNameAr || item.itemNameEn,
                // Keep original structure for backward compatibility
                invoiceDetails: item.invoiceDetails,
              };
              console.log("Processed item:", processedItem);
              return processedItem;
            } else {
              // Item is already flat or has different structure
              return item;
            }
          });
        }

        // Override totalAmount if we have items to calculate from
        // BUT preserve backend's balanceAmount calculation
        if (invoiceData.items && invoiceData.items.length > 0) {
          const subtotal = invoiceData.items.reduce((sum, item) => {
            const quantity = item.quantity || item.invoiceDetails?.quantity || 1;
            const unitPrice = item.pricePerUnit || item.invoiceDetails?.pricePerUnit || item.price || item.unitPrice || 0;
            return sum + (quantity * unitPrice);
          }, 0);

          let discount = 0;
          if (invoiceData.discountRate && invoiceData.discountRate > 0) {
            if (invoiceData.discountType === "FIXED" || invoiceData.discountType === "Fixed") {
              discount = invoiceData.discountRate;
            } else {
              discount = (subtotal * invoiceData.discountRate) / 100;
            }
          }

          const correctTotal = subtotal - discount + (invoiceData.tax || 0);

          console.log("Invoice data from backend:", {
            backendTotal: invoiceData.totalAmount,
            backendBalance: invoiceData.balanceAmount,
            backendAmountPaid: invoiceData.amountPaid,
            calculatedTotal: correctTotal,
            subtotal,
            discount,
            discountRate: invoiceData.discountRate,
            discountType: invoiceData.discountType,
            tax: invoiceData.tax || 0
          });

          invoiceData.totalAmount = correctTotal;
          // IMPORTANT: Preserve backend's balanceAmount - it's calculated correctly on the server
        }

        // Parse multiple images from comma-separated string
        if (invoiceData.image) {
          // Backend sends multiple images as comma-separated URLs
          const imageUrls = invoiceData.image.split(',').map(url => url.trim()).filter(url => url);
          invoiceData.images = imageUrls;
        } else {
          invoiceData.images = [];
        }

        setInvoice(invoiceData);

        // Check for business entity in various places
        if (invoiceData.businessEntity) {
          console.log(
            "Setting entity from businessEntity:",
            invoiceData.businessEntity
          );
          setEntity(invoiceData.businessEntity);
        } else if (invoiceData.supplier) {
          console.log("Setting entity from supplier:", invoiceData.supplier);
          setEntity(invoiceData.supplier);
        } else if (invoiceData.client) {
          console.log("Setting entity from client:", invoiceData.client);
          setEntity(invoiceData.client);
        } else {
          console.log(
            "No entity found in invoice data, will try separate fetch"
          );
        }
      } else {
        toast.error(t("Invoice not found"));
        navigate(-1);
        return null;
      }

      setLoading(false);
      return invoiceData; // Return the fetched invoice data
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      toast.error(t("Error loading invoice"));
      setLoading(false);
      navigate(-1);
      return null;
    }
  };

  const fetchEntityDetails = async () => {
    // Entity is now included in invoice.businessEntity (Android pattern)
    // Only fetch separately if not included in invoice
    if (!invoice?.accountId) return;

    // Skip if we already have entity data from invoice
    if (invoice?.businessEntity || invoice?.supplier || invoice?.client) {
      console.log("Entity already available in invoice data");
      return;
    }

    try {
      const accountType = invoice.accountType || invoice.type || "client";
      const response = await axios.get(
        `api/entitiesAccounts/getEntityAccount/${invoice.accountId}?type=${accountType}`,
        {
          headers: { Authorization: token },
        }
      );
      const entityData = response.data.items?.[0] || response.data;
      console.log("entity data:", entityData);
      setEntity(entityData);
    } catch (error) {
      console.error(
        "Error fetching entity details (CORS or network issue):",
        error
      );
      // Try to use any entity data from invoice if available
      if (invoice?.supplier) {
        console.log("Using supplier from invoice");
        setEntity(invoice.supplier);
      } else if (invoice?.client) {
        console.log("Using client from invoice");
        setEntity(invoice.client);
      }
    }
  };

  const fetchWarehouses = async () => {
    if (!invoice?.farmId) return;

    try {
      const response = await axios.get(
        `api/inventoryLocation/findByFarmId/${invoice.farmId}`,
        { headers: { Authorization: token } }
      );
      setWarehouses(response.data.items || []);
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };


  const fetchInvoiceReturns = async () => {
    if (!invoice?.id || !invoice?.accountId) return;

    try {
      // Include accountId parameter as per Android implementation
      const response = await axios.get(
        `api/invoiceReturns/list?invoiceId=${invoice.id}&accountId=${invoice.accountId}`,
        {
          headers: { Authorization: token },
        }
      );

      console.log("Raw returns response:", response.data);

      // Handle backend response structure for returns
      // Backend sends: ApiResponse with items[0] containing InvoiceReturnListDto
      // InvoiceReturnListDto has 'returns' field with list of returns
      let returnsData = [];
      if (response.data?.items?.[0]?.returns) {
        // Primary structure: items[0].returns (matches backend InvoiceReturnListDto)
        returnsData = response.data.items[0].returns;
        console.log(
          "Using items[0].returns structure - matched backend InvoiceReturnListDto"
        );
      } else if (response.data?.items?.[0]?.invoiceReturnDtos) {
        // Alternative field name that might be used
        returnsData = response.data.items[0].invoiceReturnDtos;
        console.log("Using items[0].invoiceReturnDtos structure");
      } else {
        // Fallback for different response structures
        returnsData = response.data.items || response.data || [];
        console.log("Using fallback structure - no returns found");
      }

      console.log("Processed returns data:", returnsData);

      // Ensure returnsData is an array before setting it
      const returnsArray = Array.isArray(returnsData) ? returnsData : [];
      setPreviousReturns(returnsArray);

      // Calculate total returns amount and update invoice
      const totalReturnsAmount = returnsArray.reduce((sum, returnItem) => {
        const itemReturnAmount = parseFloat(returnItem.totalReturnAmount) || 0;
        console.log(
          `Return item ${returnItem.id}: amount = ${itemReturnAmount}`
        );
        return sum + itemReturnAmount;
      }, 0);

      console.log("Total returns amount calculated:", totalReturnsAmount);
      console.log("Current invoice total:", invoice?.totalAmount);

      setInvoice((prev) => {
        const updated = {
          ...prev,
          totalReturns: totalReturnsAmount,
          // Don't set adjustedTotal here - let it be calculated in render
        };
        console.log("Updated invoice with returns:", updated);
        return updated;
      });
    } catch (error) {
      console.error("Error fetching invoice returns:", error);
    }
  };

  // Helper function to translate backend error messages
  const translateError = (errorMessage) => {
    if (!errorMessage) return errorMessage;

    // Only translate if we're in Arabic mode
    if (i18n.language !== 'ar') return errorMessage;

    // Translation map for common backend error patterns
    const translations = {
      // Field validation errors
      'FarmId is missing': 'معرف المزرعة مفقود',
      'TenantId is missing': 'معرف المستأجر مفقود',
      'InvoiceId is missing': 'معرف الفاتورة مفقود',
      'AccountId is missing': 'معرف الحساب مفقود',
      'ReturnDate is missing': 'تاريخ المرتجع مفقود',
      'Items list is missing or empty': 'قائمة العناصر مفقودة أو فارغة',
      'InvoiceItemId is missing in return items': 'معرف عنصر الفاتورة مفقود في عناصر المرتجع',
      'ReturnedQuantity is missing in return items': 'الكمية المرتجعة مفقودة في عناصر المرتجع',
      'UnitPrice is missing in return items': 'سعر الوحدة مفقود في عناصر المرتجع',

      // Invoice validation errors
      'Invoice not found': 'لم يتم العثور على الفاتورة',
      'Invoice does not belong to the provided AccountId': 'الفاتورة لا تنتمي إلى معرف الحساب المقدم',

      // Quantity validation errors (regex patterns)
      'Invoice return cannot be processed': 'لا يمكن معالجة مرتجع الفاتورة',
      'not found': 'غير موجود',
      'does not belong to invoice': 'لا ينتمي إلى الفاتورة',
      'Original quantity': 'الكمية الأصلية',
      'Already returned': 'تم إرجاعها بالفعل',
      'Remaining': 'المتبقي',
      'Cannot return': 'لا يمكن إرجاع',
      'units for item ID': 'وحدة لعنصر رقم',
      'units of': 'وحدة من',
      'to supplier': 'إلى المورد',
      'Only': 'فقط',
      'units available in stock': 'وحدة متاحة في المخزون',

      // Amount validation
      'Total return amount mismatch': 'عدم تطابق إجمالي مبلغ المرتجع',
      'Expected': 'المتوقع',
      'Provided': 'المقدم',

      // Generic errors
      'An error occurred': 'حدث خطأ',
    };

    let translatedMessage = errorMessage;

    // Replace exact matches first
    Object.keys(translations).forEach(key => {
      if (translatedMessage === key) {
        translatedMessage = translations[key];
      }
    });

    // If still in English, try partial replacements for complex messages
    if (translatedMessage === errorMessage) {
      Object.keys(translations).forEach(key => {
        const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        translatedMessage = translatedMessage.replace(regex, translations[key]);
      });
    }

    return translatedMessage;
  };

  const processReturns = async () => {
    if (processingReturn) return; // Prevent double submission

    const hasReturns = Object.values(returnQuantities).some((q) => q > 0);
    if (!hasReturns) {
      toast.error(t("Please select items to return"));
      return;
    }
    if (!returnReason.trim()) {
      toast.error(t("Please enter a return reason"));
      return;
    }

    setProcessingReturn(true); // Set loading state
    try {
      const returnItems = [];
      invoice.items?.forEach((item, index) => {
        const returnQty = returnQuantities[item.id || index] || 0;
        if (returnQty > 0) {
          const unitPrice =
            item.pricePerUnit ||
            item.invoiceDetails?.pricePerUnit ||
            item.price ||
            item.unitPrice ||
            0;

          const itemName =
            i18n.language === "ar"
              ? item.itemNameAr ||
                item.nameAr ||
                item.name ||
                item.description ||
                item.invoiceDetails?.description
              : item.itemNameEn ||
                item.nameEn ||
                item.name ||
                item.description ||
                item.invoiceDetails?.description;

          returnItems.push({
            invoiceItemId: item.id || item.invoiceDetails?.id,
            productName: itemName || item.productName || item.description,
            returnedQuantity: returnQty,
            unitPrice: unitPrice,
            totalReturnedValue: returnQty * unitPrice, // Backend expects totalReturnedValue
            returnReason: returnReason,
            // Note: productId removed as backend doesn't use it
          });
        }
      });

      const totalReturnAmount = returnItems.reduce(
        (sum, item) => sum + (item.totalReturnedValue || 0),
        0
      );

      // Match backend InvoiceReturnDto structure
      const returnData = {
        tenantId: invoice.tenantId || null, // Get from invoice or session
        farmId: invoice.farmId || null, // Get from invoice or session
        invoiceId: invoice.id,
        accountId: invoice.accountId,
        returnReason: returnReason,
        totalReturnAmount: totalReturnAmount,
        returnDate: Date.now(), // Use timestamp
        items: returnItems, // Backend expects 'items' field, not 'returnItemsList'
      };

      // Use correct endpoint as per Android implementation
      // Backend returns HTTP 200 with status field in response body
      const response = await axios.post("api/invoiceReturns/addInvoiceReturn", returnData, {
        headers: { Authorization: token },
      });

      // Check the API status in response body (backend always returns HTTP 200)
      const apiStatus = response.data?.status;
      const apiMessage = response.data?.message;

      if (apiStatus === 200) {
        // Success
        toast.success(t("Returns processed successfully"));
        setReturnMode(false);
        setReturnQuantities({});
        setReturnReason("");

        // Refresh data to show updated returns
        await fetchInvoiceDetails();
        await fetchInvoiceReturns();
      } else if (apiStatus === 400) {
        // Validation errors - translate and show detailed message
        const errorMessage = apiMessage || t("Validation failed");

        // Check if error message contains multiple validation errors (separated by newlines)
        if (errorMessage.includes('\n')) {
          // Multi-line error - translate each line and show in a more readable format
          const errors = errorMessage
            .split('\n')
            .filter(e => e.trim() && !e.includes('cannot be processed:'))
            .map(err => translateError(err.trim()));

          toast.error(
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <strong>{t("Cannot process return")}:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px', textAlign: 'left', marginBottom: 0 }}>
                {errors.map((err, idx) => (
                  <li key={idx} style={{ marginBottom: '4px' }}>{err}</li>
                ))}
              </ul>
            </div>,
            { duration: 8000 }
          );
        } else {
          // Single error message - translate it
          toast.error(translateError(errorMessage), { duration: 6000 });
        }
      } else if (apiStatus === 404) {
        toast.error(translateError(apiMessage) || t("Invoice or invoice items not found"), { duration: 4000 });
      } else if (apiStatus === 500) {
        toast.error(`${t("Server error")}: ${translateError(apiMessage) || t("Internal server error")}`, { duration: 5000 });
      } else {
        // Any other non-200 status
        toast.error(translateError(apiMessage) || t("Error processing returns"), { duration: 4000 });
      }
    } catch (error) {
      console.error("Error processing returns:", error);

      // Network or request errors (actual HTTP failures)
      if (error.request) {
        toast.error(t("Network error. Please check your connection"), { duration: 4000 });
      } else {
        toast.error(t("Error processing returns"), { duration: 4000 });
      }
    } finally {
      setProcessingReturn(false); // Reset loading state
    }
  };

  // Removed edit functionality as Android doesn't have it
  // const handleEdit = () => {
  //   navigate(`/invoices/edit/${invoiceId}`, {
  //     state: { invoice, entity },
  //   });
  // };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`api/invoice/${invoice.id}`, {
        headers: { Authorization: token },
      });
      toast.success(t("Invoice deleted successfully"));
      setShowDeleteModal(false);
      navigate(-1);
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error(t("Error deleting invoice"));
      setIsDeleting(false);
    }
  };

  const handleDeleteReturn = (returnItem) => {
    setReturnToDelete(returnItem);
    setShowDeleteReturnModal(true);
  };

  const confirmDeleteReturn = async () => {
    if (!returnToDelete) return;

    setIsDeletingReturn(true);
    try {
      await axios.delete(`api/invoiceReturns/delete/${returnToDelete.id}`, {
        headers: { Authorization: token },
      });
      toast.success(t("Return deleted successfully"));
      setShowDeleteReturnModal(false);
      setReturnToDelete(null);
      // Refresh returns list and invoice to update totals
      await fetchInvoiceReturns();
      await fetchInvoiceDetails();
    } catch (error) {
      console.error("Error deleting return:", error);
      toast.error(t("Error deleting return"));
    } finally {
      setIsDeletingReturn(false);
    }
  };

  const fetchNextInvoiceCounter = async () => {
    if (!invoice?.farmId) return;

    try {
      const response = await axios.get(
        `api/invoice/nextCounter/${invoice.farmId}`,
        {
          headers: { Authorization: token },
        }
      );
      console.log("next invoice counter", response.data);
      setNextInvoiceCounter(response.data);
    } catch (error) {
      console.error("Error fetching next invoice counter:", error);
    }
  };

  const validateInvoiceNumber = async (invoiceNumber) => {
    if (!invoice?.farmId || !invoiceNumber) return false;

    try {
      const response = await axios.post(
        "api/invoice/validateNumber",
        {
          farmId: invoice.farmId,
          invoiceNumber: invoiceNumber,
        },
        {
          headers: { Authorization: token },
        }
      );
      return response.data === true;
    } catch (error) {
      console.error("Error validating invoice number:", error);
      return false;
    }
  };

  const handlePrint = () => {
    setShowPrintDialog(true);
  }; 

  // Calculate the raw subtotal from items (before any discounts or taxes)
  const calculateRawSubtotal = () => {
    // Always prioritize calculating from items if they exist
    if (invoice.items && invoice.items.length > 0) {
      const itemsSubtotal = invoice.items.reduce((sum, item) => {
        const quantity = item.quantity || item.invoiceDetails?.quantity || 1;
        const unitPrice = item.pricePerUnit || item.invoiceDetails?.pricePerUnit || item.price || item.unitPrice || 0;
        return sum + (quantity * unitPrice);
      }, 0);
      console.log("Calculated subtotal from items:", itemsSubtotal);
      return itemsSubtotal;
    }

    // Only use these fallbacks if we truly have no items
    if (invoice.subtotal) {
      console.log("Using provided subtotal:", invoice.subtotal);
      return invoice.subtotal;
    }

    // Last resort - should rarely happen
    console.warn("No items or subtotal available, using totalAmount:", invoice.totalAmount);
    return invoice.totalAmount || invoice.total || 0;
  };

  const calculateDiscount = () => {
    // If we have a discount rate, always calculate from it
    // Don't trust discountAmount from backend as it might be wrong
    if (!invoice.discountRate || invoice.discountRate === 0) {
      // Only use discountAmount if there's no discountRate
      if (invoice.discountAmount !== undefined && invoice.discountAmount !== null) {
        console.log("Using backend discountAmount:", invoice.discountAmount);
        return invoice.discountAmount;
      }
      return 0;
    }

    // Calculate discount based on rate and type
    if (invoice.discountType === "FIXED" || invoice.discountType === "Fixed" || invoice.discountType === "fixed") {
      console.log("Fixed discount:", invoice.discountRate);
      return invoice.discountRate;
    } else {
      // Percentage discount - always calculate from raw subtotal
      const subtotal = calculateRawSubtotal();
      const discount = (subtotal * invoice.discountRate) / 100;
      console.log(`Percentage discount: ${invoice.discountRate}% of ${subtotal} = ${discount}`);
      return discount;
    }
  };

  // Calculate the final total (subtotal - discount + tax)
  const calculateFinalTotal = () => {
    const subtotal = calculateRawSubtotal();
    const discount = calculateDiscount();
    const tax = invoice.tax || 0;
    const total = subtotal - discount + tax;

    console.log("Final total calculation:", {
      subtotal,
      discount,
      tax,
      calculatedTotal: total,
      formula: `${subtotal} - ${discount} + ${tax} = ${total}`
    });

    return total;
  };

  const calculateReturnTotal = () => {
    let total = 0;
    invoice.items?.forEach((item, index) => {
      const returnQty = returnQuantities[item.id || index] || 0;
      const price =
        item.pricePerUnit ||
        item.invoiceDetails?.pricePerUnit ||
        item.price ||
        item.unitPrice ||
        0;
      total += returnQty * price;
    });
    return total;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(i18n.language === "ar" ? "ar-EG" : "en-US", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(
      i18n.language === "ar" ? "ar-EG" : "en-US",
      options
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>{t("Loading invoice...")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className={styles.container}>
        <div className={styles.notFound}>
          <h2>{t("Invoice not found")}</h2>
          <button onClick={() => navigate(-1)}>{t("Go back")}</button>
        </div>
      </div>
    );
  }

  // Calculate adjusted total after returns
  // Always calculate from items when available to avoid backend inconsistencies
  const calculatedTotal = calculateFinalTotal();

  // Use calculated total when we have items, otherwise use backend total
  const finalTotal = (invoice.items && invoice.items.length > 0)
    ? calculatedTotal
    : (invoice.totalAmount || calculatedTotal);

  console.log("Final total determination:", {
    hasItems: invoice.items && invoice.items.length > 0,
    calculatedTotal,
    backendTotal: invoice.totalAmount,
    finalTotal,
    reason: (invoice.items && invoice.items.length > 0)
      ? "Using calculated total from items"
      : "No items, using backend total"
  });

  const adjustedTotal = finalTotal - (invoice.totalReturns || 0);

  // Get the actual paid amount
  // If invoice is marked as PAID but no amount specified, assume fully paid
  const paidAmount =
    (invoice.invoiceState === "PAID" || invoice.isPaid || invoice.paymentStatus === "PAID") &&
    !invoice.amountPaid && !invoice.paidAmount
      ? adjustedTotal
      : (invoice.amountPaid || invoice.paidAmount || 0);

  // Balance calculation: adjustedTotal - amountPaid
  // Calculate locally when we have items to ensure accuracy
  // The backend's balanceAmount might be stale or incorrectly calculated
  // We should prefer local calculation when we have all the data
  // Allow negative balance to indicate overpayment/credit
  const balanceAmount =
    (invoice.items && invoice.items.length > 0)
      ? adjustedTotal - paidAmount  // Calculate from items for accuracy (can be negative)
      : (invoice.balanceAmount !== undefined && invoice.balanceAmount !== null)
        ? invoice.balanceAmount  // Use backend's balance only if no items
        : adjustedTotal - paidAmount;  // Fallback calculation (can be negative)

  // Debug logging
  const debugInfo = {
    rawSubtotal: calculateRawSubtotal(),
    discount: calculateDiscount(),
    discountRate: invoice.discountRate,
    discountType: invoice.discountType,
    tax: invoice.tax,
    calculatedTotal,
    backendTotalAmount: invoice.totalAmount,
    backendBalanceAmount: invoice.balanceAmount,
    finalTotal,
    totalReturns: invoice.totalReturns,
    adjustedTotal,
    paidAmount,
    balanceAmount,
    calculatedFromItems: invoice.items && invoice.items.length > 0,
    balanceSource: (invoice.items && invoice.items.length > 0)
      ? "Calculated from items"
      : (invoice.balanceAmount !== undefined && invoice.balanceAmount !== null)
        ? "Using backend balance"
        : "Fallback calculation",
    discrepancy: invoice.totalAmount && Math.abs(calculatedTotal - invoice.totalAmount) > 0.01
      ? `Backend says ${invoice.totalAmount} but calculated is ${calculatedTotal}`
      : null
  };

  console.log("Invoice calculation details:", debugInfo);

  // Warn if there's a significant discrepancy
  if (debugInfo.discrepancy) {
    console.warn("⚠️ Total amount discrepancy detected:", debugInfo.discrepancy);
  }

  // Log balance calculation source
  console.log(`💰 Balance calculation: ${debugInfo.balanceSource} = ${balanceAmount}`);
  if (invoice.balanceAmount !== undefined && invoice.balanceAmount !== null &&
      Math.abs(invoice.balanceAmount - balanceAmount) > 0.01) {
    console.warn(`⚠️ Balance discrepancy: Backend says ${invoice.balanceAmount} but calculated is ${balanceAmount}`);
  }

  const isPaid =
    invoice.invoiceState === "PAID" ||
    invoice.isPaid ||
    invoice.paymentStatus === "PAID" ||
    balanceAmount === 0;

  const isPartiallyPaid =
    !isPaid && (invoice.amountPaid > 0 || invoice.paidAmount > 0);

  const isOverpaid = balanceAmount < 0;

  const invoiceType =
    invoice.type ||
    (location.state?.accountType === "supplier" ? "purchase" : "sale");
  const isOverdue =
    invoice.dueDate && new Date(invoice.dueDate) < new Date() && !isPaid;

  // Additional fields from Android model (for future use)
  // const farmId = invoice.farmId;
  // const fieldId = invoice.fieldId;
  // const invoiceCounter = invoice.invoiceCounter;
  // const term = invoice.term || invoice.paymentTerms;

  const getPaymentTermsLabel = (terms) => {
    const termsMap = {
      ON_RECEIPT: t("On Receipt"),
      on_receipt: t("On Receipt"),
      NET_15: t("Net 15 Days"),
      15: t("15 Days"),
      NET_30: t("Net 30 Days"),
      30: t("30 Days"),
      NET_45: t("Net 45 Days"),
      45: t("45 Days"),
      NET_60: t("Net 60 Days"),
      60: t("60 Days"),
      NET_90: t("Net 90 Days"),
      90: t("90 Days"),
      CUSTOM: t("Custom"),
      custom: t("Custom"),
    };
    return termsMap[terms] || terms || t("On Receipt");
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <FaArrowLeft />
          </button>
          <div className={styles.headerInfo}>
            <h1>
              {t("Invoice")} #
              {invoice.invoiceNumber || invoice.number || `INV-${invoice.id}`}
            </h1>
            <div className={styles.headerMeta}>
              <span
                className={`${styles.invoiceType} ${
                  invoiceType === "purchase" ? styles.purchase : styles.sale
                }`}
              >
                {invoiceType === "purchase" ? t("Purchase") : t("Sale")}
              </span>
              <span
                className={`${styles.status} ${
                  isOverpaid
                    ? styles.overpaid
                    : isPaid
                    ? styles.paid
                    : isPartiallyPaid
                    ? styles.partial
                    : isOverdue
                    ? styles.overdue
                    : styles.pending
                }`}
              >
                {isOverpaid ? (
                  <>
                    <FaCheckCircle /> {t("Overpaid")}
                  </>
                ) : isPaid ? (
                  <>
                    <FaCheckCircle /> {t("Paid")}
                  </>
                ) : isPartiallyPaid ? (
                  <>
                    <FaClock /> {t("Partially Paid")}
                  </>
                ) : isOverdue ? (
                  <>
                    <FaClock /> {t("Overdue")}
                  </>
                ) : (
                  <>
                    <FaClock /> {t("Pending")}
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.headerActions}>
          {invoice.items?.length > 0 && (
            <button
              className={`${styles.actionButton} ${
                returnMode ? styles.active : ""
              }`}
              onClick={() => setReturnMode(!returnMode)}
            >
              <FaUndo />{" "}
              {returnMode ? t("Cancel Returns") : t("Process Returns")}
            </button>
          )}
          <button className={styles.actionButton} onClick={handlePrint}>
            <FaPrint /> {t("Print")}
          </button>
          <button
            className={`${styles.actionButton} ${styles.danger}`}
            onClick={() => setShowDeleteModal(true)}
          >
            <FaTrash /> {t("Delete")}
          </button>
        </div>
      </div>

      <div className={styles.invoiceContent}>
        {/* Financial Summary Cards */}
        <div className={styles.financialCards}>
          <div className={styles.financeCard}>
            <div className={styles.financeIcon}>
              <FaFileInvoice />
            </div>
            <div className={styles.financeContent}>
              <label>{t("Total Invoiced")}</label>
              <span className={styles.financeAmount}>
                {formatCurrency(finalTotal)}
              </span>
            </div>
          </div>

          <div className={`${styles.financeCard} ${styles.paidCard}`}>
            <div className={styles.financeIcon}>
              <FaCheckCircle />
            </div>
            <div className={styles.financeContent}>
              <label>{t("Amount Paid")}</label>
              <span className={styles.financeAmount}>
                {formatCurrency(paidAmount)}
              </span>
            </div>
          </div>

          {invoice.totalReturns > 0 && (
            <div className={`${styles.financeCard} ${styles.returnsCard}`}>
              <div className={styles.financeIcon}>
                <FaUndo />
              </div>
              <div className={styles.financeContent}>
                <label>{t("Total Returns")}</label>
                <span className={styles.financeAmount}>
                  {formatCurrency(invoice.totalReturns)}
                </span>
              </div>
            </div>
          )}

          <div
            className={`${styles.financeCard} ${
              balanceAmount === 0
                ? styles.settledCard
                : balanceAmount < 0
                  ? styles.creditCard
                  : styles.dueCard
            }`}
          >
            <div className={styles.financeIcon}>
              {balanceAmount === 0 || balanceAmount < 0 ? <FaCheckCircle /> : <FaClock />}
            </div>
            <div className={styles.financeContent}>
              <label>
                {balanceAmount < 0 ? t("Credit Balance") : t("Balance Due")}
              </label>
              <span className={styles.financeAmount}>
                {formatCurrency(Math.abs(balanceAmount))}
              </span>
              {balanceAmount > 0 && isOverdue && (
                <span className={styles.overdueTag}>{t("Overdue")}</span>
              )}
            </div>
          </div>
        </div>

        {/* Info Section - Invoice Details and Entity Side by Side */}
        <div className={styles.infoSection}>
          {/* Invoice Details Card */}
          <div className={styles.infoCard}>
            <h3 className={styles.cardTitle}>
              <FaInfoCircle /> {t("Invoice Details")}
            </h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoRow}>
                <label>{t("Invoice Date")}</label>
                <span>
                  {formatDate(
                    invoice.invoiceDate || invoice.date || invoice.createTs
                  )}
                </span>
              </div>
              {invoice.dueDate && (
                <div className={styles.infoRow}>
                  <label>{t("Due Date")}</label>
                  <span className={isOverdue ? styles.overdueText : ""}>
                    {formatDate(invoice.dueDate)}
                    {isOverdue && (
                      <span className={styles.overdueBadge}>
                        {t("Overdue")}
                      </span>
                    )}
                  </span>
                </div>
              )}
              <div className={styles.infoRow}>
                <label>{t("Payment Terms")}</label>
                <span>{getPaymentTermsLabel(invoice.paymentTerms)}</span>
              </div>
            </div>
          </div>

          {/* Business Entity Card */}
          {(entity ||
            invoice.supplier ||
            invoice.client ||
            invoice.businessEntity) && (
            <div className={styles.infoCard}>
              <h3 className={styles.cardTitle}>
                <FaInfoCircle />{" "}
                {invoiceType === "purchase"
                  ? t("Supplier Information")
                  : t("Client Information")}
              </h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoRow}>
                  <label>{t("Name")}</label>
                  <span>
                    {entity?.name ||
                      invoice.supplier?.name ||
                      invoice.client?.name ||
                      invoice.businessEntity?.name ||
                      "—"}
                  </span>
                </div>
                {(entity?.phone ||
                  invoice.supplier?.phone ||
                  invoice.client?.phone ||
                  invoice.businessEntity?.phone) && (
                  <div className={styles.infoRow}>
                    <label>{t("Phone")}</label>
                    <span>
                      {entity?.phone ||
                        invoice.supplier?.phone ||
                        invoice.client?.phone ||
                        invoice.businessEntity?.phone}
                    </span>
                  </div>
                )}
                {(entity?.email ||
                  invoice.supplier?.email ||
                  invoice.client?.email ||
                  invoice.businessEntity?.email) && (
                  <div className={styles.infoRow}>
                    <label>{t("Email")}</label>
                    <span>
                      {entity?.email ||
                        invoice.supplier?.email ||
                        invoice.client?.email ||
                        invoice.businessEntity?.email}
                    </span>
                  </div>
                )}
                {(entity?.address ||
                  invoice.supplier?.address ||
                  invoice.client?.address ||
                  invoice.businessEntity?.address) && (
                  <div className={styles.infoRow}>
                    <label>{t("Address")}</label>
                    <span>
                      {entity?.address ||
                        invoice.supplier?.address ||
                        invoice.client?.address ||
                        invoice.businessEntity?.address}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Items Section */}
        <div className={styles.itemsSection}>
          <div className={styles.itemsCard}>
            <div className={styles.itemsHeader}>
              <h3 className={styles.cardTitle}>
                <FaFileInvoice /> {t("Invoice Items")}
              </h3>
              {invoice.items?.length > 0 && (
                <span className={styles.itemsBadge}>
                  {invoice.items.length}{" "}
                  {invoice.items.length === 1 ? t("Item") : t("Items")}
                </span>
              )}
            </div>
            {invoice.items && invoice.items.length > 0 ? (
              <div
                className={`${styles.itemsTableContainer} ${
                  returnMode ? styles.returnModeActive : ""
                }`}
              >
                <table className={styles.itemsTable}>
                  <thead>
                    <tr>
                      <th className={styles.colItemName}>{t("Item Name")}</th>
                      <th className={styles.colWarehouse}>{t("Warehouse")}</th>
                      <th className={styles.colQuantity}>{t("Qty")}</th>
                      <th className={styles.colPrice}>{t("Unit Price")}</th>
                      <th className={styles.colAmount}>{t("Amount")}</th>
                      {returnMode && (
                        <th className={styles.colReturns}>{t("Return Qty")}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, index) => {
                      console.log("Rendering item:", item);

                      // Match field names from backend structure
                      const itemName =
                        i18n.language === "ar"
                          ? item.itemNameAr ||
                            item.nameAr ||
                            item.productNameAr ||
                            item.name ||
                            item.productName ||
                            item.description
                          : item.itemNameEn ||
                            item.nameEn ||
                            item.productNameEn ||
                            item.name ||
                            item.productName ||
                            item.description;

                      // Use pricePerUnit field - check both flattened and nested structure
                      const unitPrice =
                        item.pricePerUnit ||
                        item.invoiceDetails?.pricePerUnit ||
                        item.price ||
                        item.unitPrice ||
                        0;

                      const quantity =
                        item.quantity || item.invoiceDetails?.quantity || 1;

                      // Use totalAmount field if available, otherwise calculate
                      const itemTotal =
                        item.totalAmount ||
                        item.invoiceDetails?.totalAmount ||
                        quantity * unitPrice;

                      // Get warehouse information
                      const locationId = item.locationId || item.invoiceDetails?.locationId;
                      const warehouse = warehouses.find(w => w.id === locationId);
                      const warehouseName = warehouse
                        ? (i18n.language === "ar" ? warehouse.nameAr : warehouse.nameEn)
                        : "—";

                      console.log(
                        `Item ${index}: name="${itemName}", warehouse="${warehouseName}", price=${unitPrice}, qty=${quantity}, total=${itemTotal}, rawItem:`,
                        item
                      );

                      return (
                        <tr
                          key={item.id || index}
                          className={
                            index % 2 === 0 ? styles.evenRow : styles.oddRow
                          }
                        >
                          <td className={styles.colItemName}>
                            {itemName ||
                              item.description ||
                              `${t("Item")} ${index + 1}`}
                          </td>
                          <td className={styles.colWarehouse}>
                            {warehouseName}
                          </td>
                          <td className={styles.colQuantity}>{quantity}</td>
                          <td className={styles.colPrice}>
                            {formatCurrency(unitPrice)}
                          </td>
                          <td className={styles.colAmount}>
                            {formatCurrency(itemTotal)}
                          </td>
                          {returnMode && (
                            <td className={styles.colReturns}>
                              <input
                                type="number"
                                className={styles.returnInput}
                                min="0"
                                max={quantity}
                                value={returnQuantities[item.id || index] || ""}
                                placeholder="0"
                                onChange={(e) => {
                                  const value = Math.min(
                                    parseInt(e.target.value) || 0,
                                    quantity
                                  );
                                  setReturnQuantities((prev) => ({
                                    ...prev,
                                    [item.id || index]: value,
                                  }));
                                }}
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles.noItems}>
                <p>{t("No items in this invoice")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Return Mode Panel */}
        {returnMode && invoice.items?.length > 0 && (
          <div className={styles.returnPanel}>
            <div className={styles.returnHeader}>
              <div className={styles.returnTitle}>
                <FaUndo className={styles.returnIcon} />
                <h4>{t("Process Returns")}</h4>
              </div>
              <div className={styles.returnAmount}>
                <span className={styles.returnLabel}>{t("Total Return")}:</span>
                <span className={styles.returnValue}>
                  {formatCurrency(calculateReturnTotal())}
                </span>
              </div>
            </div>

            <div className={styles.returnBody}>
              <div className={styles.returnReason}>
                <label>{t("Return Reason")}</label>
                <textarea
                  className={styles.returnReasonInput}
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder={t("Please provide a reason for the return")}
                  rows="3"
                />
              </div>

              <div className={styles.returnActions}>
                <button
                  className={`${styles.returnButton} ${styles.confirmReturn}`}
                  onClick={processReturns}
                  disabled={
                    processingReturn ||
                    !returnReason.trim() ||
                    calculateReturnTotal() === 0
                  }
                >
                  {processingReturn ? (
                    <>
                      <span className={styles.spinner}></span>
                      {t("Processing...")}
                    </>
                  ) : (
                    <>
                      <FaCheckCircle /> {t("Confirm Return")}
                    </>
                  )}
                </button>
                <button
                  className={`${styles.returnButton} ${styles.cancelReturn}`}
                  onClick={() => {
                    setReturnMode(false);
                    setReturnQuantities({});
                    setReturnReason("");
                  }}
                >
                  {t("Cancel")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Financial Summary */}
      <div className={styles.summarySection}>
        {/* Always show subtotal if there's a discount or tax */}
        {(invoice.discountRate > 0 || invoice.discountAmount > 0 || invoice.tax > 0) && (
          <>
            <div className={styles.summaryRow}>
              <span>{t("Subtotal")}</span>
              <span>
                {formatCurrency(calculateRawSubtotal())}
              </span>
            </div>

            {/* Show discount if exists */}
            {(invoice.discountRate > 0 || invoice.discountAmount > 0) && (
              <div className={styles.summaryRow}>
                <span>
                  {t("Discount")}
                  {(invoice.discountType === "PERCENTAGE" || invoice.discountType === "percentage" ||
                    (!invoice.discountType && invoice.discountRate <= 100))
                    ? ` (${invoice.discountRate || 0}%)`
                    : ""}
                </span>
                <span className={styles.discount}>
                  -{formatCurrency(calculateDiscount())}
                </span>
              </div>
            )}

            {/* Show tax if exists */}
            {invoice.tax > 0 && (
              <div className={styles.summaryRow}>
                <span>{t("Tax")}</span>
                <span>{formatCurrency(invoice.tax || 0)}</span>
              </div>
            )}

            <div className={styles.divider}></div>
          </>
        )}

        {/* Total (after discount and tax) */}
        <div className={styles.summaryRow}>
          <span className={styles.totalLabel}>{t("Total")}</span>
          <span className={styles.totalAmount}>
            {formatCurrency(finalTotal)}
          </span>
        </div>

        {/* Total Returns if any */}
        {invoice.totalReturns > 0 && (
          <>
            <div className={styles.divider}></div>
            <div className={styles.summaryRow}>
              <span className={styles.totalReturns}>{t("Total Returns")}</span>
              <span className={styles.returnsAmount}>
                -{formatCurrency(invoice.totalReturns)}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.totalLabel}>{t("Adjusted Total")}</span>
              <span className={styles.totalAmount}>
                {formatCurrency(adjustedTotal)}
              </span>
            </div>
          </>
        )}

        {/* Payment and Balance Details - Always show if there's a payment or balance */}
        {(paidAmount > 0 || balanceAmount > 0 || invoice.invoiceState === "PAID") && (
          <>
            <div className={styles.divider}></div>
            <div className={styles.summaryRow}>
              <span>{t("Amount Paid")}</span>
              <span className={styles.paidAmount}>
                {formatCurrency(paidAmount)}
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.totalLabel}>
                {balanceAmount < 0 ? t("Credit Balance") : t("Balance Due")}
              </span>
              <span className={`${styles.totalAmount} ${
                balanceAmount > 0
                  ? styles.dueAmount
                  : balanceAmount < 0
                    ? styles.creditAmount
                    : styles.settledAmount
              }`}>
                {balanceAmount < 0 && "("}
                {formatCurrency(Math.abs(balanceAmount))}
                {balanceAmount < 0 && ")"}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Previous Returns Section */}
      {(previousReturns?.length > 0 || invoice.totalReturns > 0) && (
        <div className={styles.previousReturnsSection}>
          <div className={styles.previousReturnsHeader}>
            <h3>
              <FaUndo /> {t("Previous Returns")}
              {previousReturns?.length > 0 && ` (${previousReturns.length})`}
            </h3>
            {previousReturns?.length > 3 && (
              <button
                className={styles.viewAllBtn}
                onClick={() => setShowAllReturns(!showAllReturns)}
              >
                {showAllReturns ? t("Show Less") : t("View All")} →
              </button>
            )}
          </div>
          {previousReturns?.length > 0 ? (
            <div className={styles.returnsHistory}>
              {(showAllReturns
                ? previousReturns
                : previousReturns.slice(0, 3)
              ).map((returnItem, index) => (
                <div key={returnItem.id || index} className={styles.returnCard}>
                  <div className={styles.returnHeader}>
                    <div className={styles.returnInfo}>
                      <span className={styles.returnDate}>
                        {formatDate(returnItem.returnDate)}
                      </span>
                      <span className={styles.returnAmount}>
                        {formatCurrency(returnItem.totalReturnAmount)}
                      </span>
                    </div>
                    <button
                      className={styles.deleteReturnBtn}
                      onClick={() => handleDeleteReturn(returnItem)}
                      title={t("Delete return")}
                    >
                      <FaTrash />
                    </button>
                  </div>
                  {returnItem.returnReason && (
                    <div className={styles.returnReason}>
                      {returnItem.returnReason}
                    </div>
                  )}
                  <div className={styles.returnItems}>
                    {(returnItem.items || returnItem.returnItemsList || [])?.map(
                      (item, idx) => (
                        <div key={idx} className={styles.returnItem}>
                          <span className={styles.itemName}>
                            {item.productName || `${t("Item")} ${idx + 1}`}
                          </span>
                          <span className={styles.itemQuantity}>
                            {item.returnedQuantity || item.quantity} ×{" "}
                            {formatCurrency(item.unitPrice || item.price)}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : invoice.totalReturns > 0 ? (
            <div className={styles.returnsSummary}>
              <p>
                {t("Total Returns")}: {formatCurrency(invoice.totalReturns)}
              </p>
              <button
                className={styles.viewReturnsBtn}
                onClick={() => {
                  // Fetch returns history
                  fetchInvoiceReturns();
                }}
              >
                {t("Load Returns History")}
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* Attachments Section */}
      {invoice.images?.length > 0 && (
        <div className={styles.attachmentsSection}>
          <h3>{t("Attachments")}</h3>
          <div className={styles.imageGallery}>
            {invoice.images.map((image, index) => (
              <div key={index} className={styles.imageItem}>
                <img
                  src={image.url || image}
                  alt={`${t("Attachment")} ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes Section */}
      {invoice.notes && (
        <div className={styles.notesSection}>
          <h3>{t("Notes")}</h3>
          <p>{invoice.notes}</p>
        </div>
      )}

      {/* Bottom Action Button */}
      {!isPaid && !isOverpaid && (
        <div className={styles.bottomAction}>
          <button
            className={styles.paymentButton}
            onClick={() => {
              navigate("/transactions/new", {
                state: {
                  type: invoiceType === "purchase" ? "expense" : "income",
                  showInvoiceSelection: true,
                  selectedInvoice: invoice,
                  accountId: invoice.accountId,
                  entity: entity || invoice.businessEntity || invoice.supplier || invoice.client,
                  accountType: invoice.accountType || invoice.type || invoiceType,
                  prefilledData: {
                    category: t("Invoice Payment"),
                    description: `${t("Invoice")} #${
                      invoice.invoiceNumber || invoice.number || invoice.id
                    }`,
                    date: new Date().toISOString().split("T")[0],
                    amount: Math.abs(balanceAmount),
                  },
                },
              });
            }}
          >
            {invoiceType === "purchase"
              ? t("Add Payment")
              : t("Receive Payment")}
          </button>
        </div>
      )}

      {/* Show refund button if overpaid - HIDDEN until backend implements proper refund handling */}
      {/* {isOverpaid && (
        <div className={styles.bottomAction}>
          <button
            className={styles.paymentButton}
            onClick={() => {
              navigate("/transactions/new", {
                state: {
                  type: invoiceType === "purchase" ? "income" : "expense",  // Reverse type for refund
                  // IMPORTANT: Don't link refund to invoice to avoid double-counting
                  // The backend sums all payments linked to an invoice, including refunds
                  showInvoiceSelection: false,  // Don't select invoice for refunds
                  selectedInvoice: null,  // No invoice linkage for refunds
                  accountId: invoice.accountId,
                  entity: entity || invoice.businessEntity || invoice.supplier || invoice.client,
                  accountType: invoice.accountType || invoice.type || invoiceType,
                  prefilledData: {
                    category: t("Invoice Refund"),
                    description: `${t("Refund for Invoice")} #${
                      invoice.invoiceNumber || invoice.number || invoice.id
                    } - ${entity?.name || invoice.businessEntity?.name || invoice.supplier?.name || invoice.client?.name || ""}`,
                    date: new Date().toISOString().split("T")[0],
                    amount: Math.abs(balanceAmount),
                  },
                },
              });
            }}
          >
            {invoiceType === "purchase"
              ? t("Receive Refund")
              : t("Issue Refund")}
          </button>
        </div>
      )} */}

      {/* Print Modal */}
      {showPrintDialog && (
        <InvoicePrintModal
          invoice={invoice}
          entity={entity}
          onClose={() => setShowPrintDialog(false)}
        />
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.deleteModal}>
          <div className={styles.deleteModalContent}>
            <div className={styles.deleteModalHeader}>
              <div className={styles.deleteModalIcon}>
                <FaTrash />
              </div>
              <h2>{t("Confirm Delete")}</h2>
            </div>

            <div className={styles.deleteModalBody}>
              <p>{t("Are you sure you want to delete this invoice?")}</p>
              <div className={styles.invoiceInfo}>
                <div className={styles.infoItem}>
                  <span>{t("Invoice Number")}:</span>
                  <strong>{invoice.invoiceNumber || invoice.number || invoice.id}</strong>
                </div>
                <div className={styles.infoItem}>
                  <span>{t("Date")}:</span>
                  <strong>{formatDate(invoice.invoiceDate || invoice.date)}</strong>
                </div>
                <div className={styles.infoItem}>
                  <span>{t("Amount")}:</span>
                  <strong>{formatCurrency(finalTotal)}</strong>
                </div>
              </div>
              <div className={styles.warningMessage}>
                <FaInfoCircle />
                <p>{t("This action cannot be undone")}</p>
              </div>
            </div>

            <div className={styles.deleteModalFooter}>
              <button
                className={styles.cancelBtn}
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t("Cancel")}
              </button>
              <button
                className={styles.deleteBtn}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className={styles.spinner}></span>
                    {t("Deleting...")}
                  </>
                ) : (
                  <>
                    <FaTrash /> {t("Delete Invoice")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Return Confirmation Modal */}
      {showDeleteReturnModal && returnToDelete && (
        <div className={styles.deleteModal}>
          <div className={styles.deleteModalContent}>
            <div className={styles.deleteModalHeader}>
              <div className={styles.deleteModalIcon}>
                <FaTrash />
              </div>
              <h2>{t("Confirm Delete Return")}</h2>
            </div>

            <div className={styles.deleteModalBody}>
              <p>{t("Are you sure you want to delete this return?")}</p>
              <div className={styles.invoiceInfo}>
                <div className={styles.infoItem}>
                  <span>{t("Return Date")}:</span>
                  <strong>{formatDate(returnToDelete.returnDate)}</strong>
                </div>
                <div className={styles.infoItem}>
                  <span>{t("Amount")}:</span>
                  <strong>{formatCurrency(returnToDelete.totalReturnAmount)}</strong>
                </div>
                {returnToDelete.returnReason && (
                  <div className={styles.infoItem}>
                    <span>{t("Reason")}:</span>
                    <strong>{returnToDelete.returnReason}</strong>
                  </div>
                )}
              </div>
              <div className={styles.warningMessage}>
                <FaInfoCircle />
                <p>{t("This action will also restore the returned items to inventory")}</p>
              </div>
            </div>

            <div className={styles.deleteModalFooter}>
              <button
                className={styles.cancelBtn}
                onClick={() => {
                  setShowDeleteReturnModal(false);
                  setReturnToDelete(null);
                }}
                disabled={isDeletingReturn}
              >
                {t("Cancel")}
              </button>
              <button
                className={styles.deleteBtn}
                onClick={confirmDeleteReturn}
                disabled={isDeletingReturn}
              >
                {isDeletingReturn ? (
                  <>
                    <span className={styles.spinner}></span>
                    {t("Deleting...")}
                  </>
                ) : (
                  <>
                    <FaTrash /> {t("Delete Return")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetails;
